import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Loading from '../../components/Loading';
import LessonBuilder from '../../components/LessonBuilder';
import { fromLegacy, itemProblem, type BuilderItem } from '../../lib/grammar/builder';
import { deleteAssignment, fetchVideoSubtitles, saveAssignment } from '../../lib/api';
import { cleanImages, removeImages, type AssignmentImage } from '../../lib/assignmentImages';
import ImageAttach from '../../components/ImageAttach';
import { languageName } from '../../lib/types';
import { addDays, dayInZone, parseYmd, shortDate, today, weekStartOf } from '../../lib/dates';
import { isOut } from '../../lib/kinds';
import { findStory, type LibraryStory } from '../../lib/library';
import { DEFAULT_WPM, PACES, clock, minReadSeconds, paceLabel, wordCount } from '../../lib/reading';
import { cleanStoryQuestions, needsLesson, needsReadingTime, sentences } from '../../lib/storyLesson';
import { parseSubtitles, type Cue } from '../../lib/subtitles';
import {
  DEFAULT_VERBS, LEVELS, TOPICS, builderOnly, levelFor, parseCustom, parseVerbList, topicsFor, verbProblems, type GrammarConfig, type TopicId,
} from '../../lib/grammar/topics';
import type { AssignmentKind, StoryMode } from '../../lib/types';
import { checkEmbeddable, fetchVideoTitle, parseYouTubeId, thumbnailUrl, type EmbedCheck } from '../../lib/youtube';
import { useClassroom } from '../../state/Classroom';

/** New assignment (/assign?kind=video) or edit (/assign/:id). */
const clampInt = (v: string, lo: number, hi: number) => Math.max(lo, Math.min(hi, Math.round(Number(v) || 0)));

export default function Assign() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { hydrated, assignments, drafts, active, canCreate } = useClassroom();
  if (!hydrated) return <Loading />;
  if (!active) return <div className="notice">Create a class first. <Link to="/">Home</Link></div>;

  const existing = id ? assignments.find(a => a.id === id) ?? drafts.find(a => a.id === id) : undefined;
  if (!canCreate) {
    return (
      <div className="narrow">
        <h1>{id ? 'Edit assignment' : 'New assignment'}</h1>
        <div className="notice">
          Your free trial has ended, so assignments can’t be made or changed. Everything already assigned still
          works for your students. <Link to="/billing">Subscribe</Link> to keep going.
        </div>
      </div>
    );
  }
  if (id && !existing) return <div className="notice">That assignment no longer exists. <Link to="/assignments">All assignments</Link></div>;

  const asked = params.get('kind');
  const kind = (existing?.kind ?? (asked === 'story' || asked === 'grammar' ? asked : 'video')) as AssignmentKind;
  const lib = !id ? params.get('lib') : null;
  if (lib) return <FromLibrary key={lib} libId={lib} />;
  return <AssignForm key={id ?? kind} id={id} initialKind={kind} />;
}

/** A library story opened in the form, everything filled in (lib/library). */
function FromLibrary({ libId }: { libId: string }) {
  const [preset, setPreset] = useState<LibraryStory | null | undefined>(undefined);
  useEffect(() => { findStory(libId).then(s => setPreset(s ?? null)).catch(() => setPreset(null)); }, [libId]);
  if (preset === undefined) return <Loading what="the story" />;
  if (!preset) return <div className="notice">That library story wasn’t found. <Link to="/library">Library</Link></div>;
  return <AssignForm initialKind="story" preset={preset} />;
}

function AssignForm({ id, initialKind, preset }: { id?: string; initialKind: AssignmentKind; preset?: LibraryStory }) {
  const navigate = useNavigate();
  const { assignments, drafts, active, refresh } = useClassroom();
  const existing = id ? assignments.find(a => a.id === id) ?? drafts.find(a => a.id === id) : undefined;
  // Already sent to students: it stays out (its due date can still move). Otherwise (new,
  // a draft, or scheduled) the teacher picks when it goes out. No weeks: just a due date.
  const wasOut = !!existing && isOut(existing, today());
  const classToday = dayInZone(new Date(), active?.timezone);
  const [when, setWhen] = useState<'now' | 'later'>(existing?.publish_on && !wasOut ? 'later' : 'now');
  const [publishOn, setPublishOn] = useState(
    existing?.publish_on && existing.publish_on > classToday ? existing.publish_on : addDays(classToday, 1),
  );
  const asDraft = useRef(false);

  const [kind, setKind] = useState<AssignmentKind>(initialKind);
  const [title, setTitle] = useState(existing?.title ?? preset?.title ?? '');
  // Every assignment has a due date: a week after it goes out unless the teacher picks another.
  const [due, setDue] = useState(existing?.due_date ?? addDays(dayInZone(new Date(), active?.timezone), 7));
  const [video, setVideo] = useState(existing?.video_id ? `https://youtu.be/${existing.video_id}` : '');
  const [story, setStory] = useState(existing?.story_text ?? preset?.text ?? '');
  const [instructions, setInstructions] = useState(existing?.instructions ?? '');
  const [images, setImages] = useState<AssignmentImage[]>(() => cleanImages(existing?.images));
  const [times, setTimes] = useState(existing?.required_count ?? preset?.times ?? 1);
  const [wpm, setWpm] = useState(existing?.read_wpm ?? preset?.wpm ?? DEFAULT_WPM);
  // How a story is done (20261044): the story lesson, timed reading, or both.
  const [storyMode, setStoryMode] = useState<StoryMode>(existing?.story_mode ?? 'lesson');
  // Or a different way for each read (story_plan): e.g. read 1 a lesson, the rest timed reading.
  const [perRead, setPerRead] = useState(!!existing?.story_plan?.length);
  const [plan, setPlan] = useState<StoryMode[]>(() => Array.from({ length: 10 }, (_, i) => existing?.story_plan?.length ? existing.story_plan[Math.min(i, existing.story_plan.length - 1)] : existing?.story_mode ?? 'lesson'));
  const planOn = perRead && times > 1;
  const modesUsed: StoryMode[] = planOn ? [...new Set(plan.slice(0, times))] : [storyMode];
  // Share the lesson out over the reads (story_split), so it isn't the whole story every day.
  const [split, setSplit] = useState(!!existing?.story_split);
  // Only class language → students' language in the lesson (story_one_way).
  const [oneWay, setOneWay] = useState(!!existing?.story_one_way);
  const lessonReads = planOn ? plan.slice(0, times).filter(needsLesson).length : needsLesson(storyMode) ? times : 0;
  const canSplit = lessonReads >= 2;
  // Story only: the teacher's own questions, asked at the end of the story lesson.
  const [storyQs, setStoryQs] = useState<DraftQ[]>(() => (existing?.story_questions ?? []).map(q => ({ q: q.q, options: [...q.options, '', '', '', ''].slice(0, 4), answer: q.answer })));
  const [onePerDay, setOnePerDay] = useState(existing?.one_per_day ?? false);
  // Sand dollars paid automatically (see supabase/migrations/20261014_assignment_rewards.sql).
  const [sdPer, setSdPer] = useState(existing?.sd_per_pass ?? 0);
  const [sdDone, setSdDone] = useState(existing?.sd_on_complete ?? 0);
  const [subsText, setSubsText] = useState(() => cuesToText(existing?.subtitles ?? null));
  const cues = useMemo(() => parseSubtitles(subsText), [subsText]);
  // What happened when we asked YouTube (via fetch-subtitles) for the video's
  // own subtitles. Only tried when the box is empty, so editing an assignment
  // or pasting by hand never spends a credit.
  const [autoSubs, setAutoSubs] = useState<
    'idle' | 'loading' | 'ok' | 'none' | 'wrong_language' | 'unavailable'
  >('idle');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Grammar settings ─────────────────────────────────────────────────────
  const g0 = existing?.grammar;
  const classLang = active?.language ?? 'es';
  const [gTopics, setGTopics] = useState<TopicId[]>(g0?.topics ?? [topicsFor(classLang)[0]]);
  const [gVerbs, setGVerbs] = useState<Record<VerbTopic, string>>(() => ({
    present_regular: (g0?.verbs?.present_regular ?? []).join(', '),
    present_stem: (g0?.verbs?.present_stem ?? []).join(', '),
    preterite_regular: (g0?.verbs?.preterite_regular ?? []).join(', '),
  }));
  const [gVosotros, setGVosotros] = useState(g0?.vosotros ?? false);
  // Older assignments kept their questions as text lines; they open as builder cards.
  const [gBuilder, setGBuilder] = useState<BuilderItem[]>(() => g0?.builder ?? fromLegacy(parseCustom(g0?.custom)));
  const [gLength, setGLength] = useState(g0?.length ?? 10);
  const [gLevel, setGLevel] = useState(g0?.difficulty ?? 3);
  const [gClimb, setGClimb] = useState(g0?.difficultyTo != null);
  const [gTo, setGTo] = useState(g0?.difficultyTo ?? 5);
  const climbing = gClimb && gLevel < 5 && gTo > gLevel;
  const builderReady = useMemo(() => gBuilder.filter(it => !itemProblem(it)).length, [gBuilder]);
  const onlyBuilder = builderOnly({ topics: gTopics });
  const verbIssues = useMemo(() => gTopics.flatMap(t =>
    (t in DEFAULT_VERBS) ? verbProblems(t as VerbTopic, parseVerbList(gVerbs[t as VerbTopic])) : []), [gTopics, gVerbs]);
  const grammarConfig: GrammarConfig = {
    topics: gTopics,
    verbs: Object.fromEntries(
      (Object.keys(gVerbs) as VerbTopic[]).map(t => [t, parseVerbList(gVerbs[t])]).filter(([, v]) => v.length),
    ),
    vosotros: gVosotros,
    builder: gTopics.includes('custom') && gBuilder.length ? gBuilder : undefined,
    length: gLength,
    // A builder-only lesson keeps the formats the teacher chose, so it has no difficulty.
    difficulty: onlyBuilder ? undefined : gLevel,
    difficultyTo: climbing && !onlyBuilder ? gTo : undefined,
  };
  const toggleTopic = (t: TopicId) =>
    setGTopics(prev => (prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]));

  const videoId = parseYouTubeId(video);

  // Test every pasted link before it can be assigned. See lib/youtube.ts.
  const [embed, setEmbed] = useState<EmbedCheck | 'checking' | null>(null);
  useEffect(() => {
    if (kind !== 'video' || !videoId) { setEmbed(null); return; }
    let cancelled = false;
    setEmbed('checking');
    (async () => {
      const meta = await fetchVideoTitle(videoId);
      if (cancelled) return;
      if (meta.title) setTitle(t => (t.trim() ? t : meta.title!.slice(0, 140)));
      if (meta.status === 401 || meta.status === 403) { setEmbed('blocked'); return; }
      if (meta.status === 404) { setEmbed('missing'); return; }
      const r = await checkEmbeddable(videoId);
      if (!cancelled) setEmbed(r);
    })();
    return () => { cancelled = true; };
  }, [kind, videoId]);

  const lang = active?.language ?? 'es';
  useEffect(() => {
    if (kind !== 'video' || !videoId || (embed !== 'ok' && embed !== 'unknown')) return;
    if (subsText.trim()) return;
    let cancelled = false;
    setAutoSubs('loading');
    fetchVideoSubtitles(videoId, lang).then(r => {
      if (cancelled) return;
      if (r.status === 'ok' && r.cues.length) {
        setSubsText(cuesToText(r.cues));
        setAutoSubs('ok');
      } else if (r.status === 'none') setAutoSubs('none');
      else if (r.status === 'wrong_language') setAutoSubs('wrong_language');
      else if (r.status === 'not_configured') setAutoSubs('idle');
      else setAutoSubs('unavailable');
    });
    return () => { cancelled = true; };
    // subsText is read, not watched: typing in the box must not re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, videoId, embed, lang]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const draft = !wasOut && asDraft.current;
    asDraft.current = false;
    const later = !wasOut && when === 'later';
    if (later && !draft) {
      if (!publishOn || publishOn <= classToday) { setError('Pick a day after today for it to go out, or choose “Now”.'); return; }
      if (due && due < publishOn) { setError('The due date is before the day it goes out.'); return; }
    }
    if (!draft && !due) { setError('Pick a due date.'); return; }
    if (!draft && !wasOut && !later && due < classToday) { setError('The due date has already passed. Pick today or a later day.'); return; }
    if (kind === 'video' && !videoId) { setError('Paste a YouTube link — that one could not be read.'); return; }
    if (kind === 'video' && embed === 'missing') {
      setError('That video is private or deleted. Pick a different one.');
      return;
    }
    if (kind === 'story' && !story.trim()) { setError('Paste the story text.'); return; }
    if (kind === 'story') {
      const bad = storyQs.findIndex(q => cleanStoryQuestions([q]).length === 0);
      if (bad >= 0) { setError(`Story question ${bad + 1} needs the question, at least 2 answers, and the right one ticked (or remove it).`); return; }
    }
    if (kind === 'grammar') {
      if (gTopics.length === 0) { setError('Choose at least one grammar topic.'); return; }
      if (gTopics.includes('custom') && builderReady === 0) { setError('Finish at least one question in the Custom Lesson Builder, or untick it.'); return; }
      if (onlyBuilder && builderReady < 5) { setError('A lesson needs at least 5 finished questions to count. Add more, or add a ready-made topic too.'); return; }
      if (verbIssues.length) { setError(verbIssues[0]); return; }
    }
    if (kind === 'video' && subsText.trim() && cues.length === 0) {
      setError('The subtitles couldn’t be read. Paste them with their timestamps (see the steps above the box).');
      return;
    }
    setBusy(true);
    try {
      const saved = await saveAssignment({
        classroom_id: active!.id,
        kind,
        title: title.trim(),
        instructions: instructions.trim() || null,
        // week_start is only bookkeeping now (the Monday of the day it goes out); nobody picks a week.
        week_start: wasOut ? existing!.week_start : later && publishOn ? weekStartOf(parseYmd(publishOn)) : weekStartOf(),
        publish_on: wasOut ? existing!.publish_on ?? null : later ? publishOn || null : null,
        draft,
        due_date: due || null,
        video_id: kind === 'video' ? videoId : null,
        story_text: kind === 'story' ? story.trim() : null,
        read_wpm: wpm,
        // Only sent when there are (or were) questions, so saving works even before 20261043 is run.
        ...(kind === 'story' && (storyQs.length || existing?.story_questions?.length) ? { story_questions: cleanStoryQuestions(storyQs) } : {}),
        // Only sent when it isn't the default, so saving works even before 20261044 is run.
        ...(kind === 'story' && (planOn || storyMode !== 'lesson' || (existing?.story_mode ?? 'lesson') !== 'lesson' || existing?.story_plan?.length)
          ? { story_mode: planOn ? plan[0] : storyMode, story_plan: planOn ? plan.slice(0, times) : null } : {}),
        ...(kind === 'story' && (split || existing?.story_split) ? { story_split: split && canSplit } : {}),
        // Only sent when on (or was), so saving works even before 20261050 is run.
        ...(kind === 'story' && (oneWay || existing?.story_one_way) ? { story_one_way: oneWay } : {}),
        grammar: kind === 'grammar' ? grammarConfig : null,
        required_count: times,
        one_per_day: onePerDay,
        sd_per_pass: sdPer,
        sd_on_complete: sdDone,
        // Only sent when there are (or were) pictures, so saving works even before 20261033 is run.
        ...(images.length || cleanImages(existing?.images).length ? { images: images.map(i => ({ ...i, caption: i.caption?.trim() || undefined })) } : {}),
        subtitles: kind === 'video' && cues.length > 0 ? cues : null,
        // Record what we know, so opening the assignment doesn't ask again.
        // A changed video or an empty box goes back to "not checked yet".
        ...(kind === 'video' && cues.length > 0
          ? { subtitles_status: autoSubs === 'ok' ? 'ok' as const : 'manual' as const, subtitles_checked_at: new Date().toISOString() }
          : kind === 'video' && (autoSubs === 'none' || autoSubs === 'wrong_language')
            ? { subtitles_status: autoSubs, subtitles_checked_at: new Date().toISOString() }
            : { subtitles_status: null, subtitles_checked_at: null }),
      }, id);
      // Pictures taken off the assignment: delete their files now it's saved without them.
      const kept = new Set(images.map(i => i.path));
      void removeImages(cleanImages(existing?.images).map(i => i.path).filter(p => !kept.has(p))).catch(() => {});
      await refresh();
      navigate(`/a/${saved.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!id || !window.confirm('Delete this assignment? Students’ progress on it is deleted too.')) return;
    setBusy(true);
    try {
      await deleteAssignment(id);
      void removeImages(cleanImages(existing?.images).map(i => i.path)).catch(() => {});
      await refresh();
      navigate('/assignments');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div className="narrow">
      <div className="crumbs"><Link to="/assignments">← All assignments</Link></div>
      <h1>{!id ? 'New assignment' : existing?.draft ? 'Edit draft' : 'Edit assignment'}</h1>
      {preset && (
        <div className="notice small good" style={{ marginBottom: 12 }}>
          📚 From the library: <strong>{preset.title}</strong> ({preset.level}). Check the settings, then assign it or save it as a draft.
        </div>
      )}
      <p className="sub">For {active?.name}</p>

      <form className="panel" onSubmit={save}>
        {!id && (
          <div className="seg" style={{ marginBottom: 18 }}>
            <button type="button" className={kind === 'video' ? 'on' : ''} onClick={() => setKind('video')}>🎬 Video</button>
            <button type="button" className={kind === 'story' ? 'on' : ''} onClick={() => setKind('story')}>📖 Story</button>
            <button type="button" className={kind === 'grammar' ? 'on' : ''} onClick={() => setKind('grammar')}>🧩 Grammar</button>
          </div>
        )}

        <div className="field">
          <label htmlFor="title">Title</label>
          <input id="title" required maxLength={140} value={title} onChange={e => setTitle(e.target.value)}
            placeholder={kind === 'video' ? `Easy ${languageName(classLang)}: at the market` : kind === 'grammar' ? 'Present tense verbs' : classLang === 'es' ? 'El perro perdido' : 'The lost dog'} />
        </div>

        <div className="grid-2">
          {wasOut ? (
            <div className="field">
              <label>Went out</label>
              <span>{shortDate(existing!.publish_on ?? existing!.week_start)}</span>
            </div>
          ) : (
            <div className="field">
              <label>Goes out</label>
              <div className="seg">
                <button type="button" className={when === 'now' ? 'on' : ''} onClick={() => setWhen('now')}>Now</button>
                <button type="button" className={when === 'later' ? 'on' : ''} onClick={() => setWhen('later')}>On a later day</button>
              </div>
              {when === 'later' && (
                <input id="publish" type="date" aria-label="Day it goes out" style={{ marginTop: 8 }}
                  min={addDays(classToday, 1)} value={publishOn} onChange={e => setPublishOn(e.target.value)} />
              )}
              <span className="hint">
                {when === 'later'
                  ? publishOn ? `Students see it from ${shortDate(publishOn)}, in your class’s time zone.` : 'Pick the day.'
                  : 'Students see it as soon as you assign it.'}
              </span>
            </div>
          )}
          <div className="field">
            <label htmlFor="due">Due date</label>
            <input id="due" type="date" required value={due} onChange={e => setDue(e.target.value)} />
            <span className="hint">Students can work on it until the end of this day; after that it closes (you still see it, with their progress, under Progress → Past due).</span>
          </div>
        </div>

        <div className="field">
          <label htmlFor="times">{kind === 'video' ? 'Times to watch' : kind === 'grammar' ? 'Lessons to complete' : 'Times to read'}</label>
          <div className="stepper">
            <button type="button" onClick={() => setTimes(t => Math.max(1, t - 1))} disabled={times <= 1} aria-label="Fewer">−</button>
            <input id="times" type="number" min={1} max={10} value={times}
              onChange={e => setTimes(Math.min(10, Math.max(1, Number(e.target.value) || 1)))} />
            <button type="button" onClick={() => setTimes(t => Math.min(10, t + 1))} disabled={times >= 10} aria-label="More">+</button>
          </div>
          <span className="hint">
            {kind === 'video'
              ? 'A watch counts once 90% of the video has actually played.'
              : kind === 'grammar'
              ? 'A lesson counts when it is finished with hearts to spare. Students can always practise more.'
              : [
                  modesUsed.some(needsLesson) ? `A lesson read counts when the story lesson is finished (every question answered right; a missed one comes back a few questions later)${story.trim() ? ` (${sentences(story).length} sentences, ${sentences(story).length * (oneWay ? 1 : 2)} questions, plus your story questions)` : ''}.` : '',
                  modesUsed.some(needsReadingTime) ? `A timed read needs at least ${story.trim() ? clock(minReadSeconds(story, wpm)) : 'enough'} of active reading${story.trim() ? ` (${wordCount(story)} words at ${wpm} a minute)` : ''}, paragraph by paragraph with the page held still.` : '',
                ].filter(Boolean).join(' ')}
          </span>
        </div>

        {kind === 'story' && (
          <div className="field">
            <label>How students do the story</label>
            {times > 1 && (
              <div className="seg" role="radiogroup" aria-label="Same way every time, or different" style={{ maxWidth: 460, marginBottom: 8 }}>
                <button type="button" role="radio" aria-checked={!perRead} className={!perRead ? 'on' : ''} onClick={() => setPerRead(false)}>The same every read</button>
                <button type="button" role="radio" aria-checked={perRead} className={perRead ? 'on' : ''}
                  onClick={() => { if (!perRead) setPlan(p => p.map(() => storyMode)); setPerRead(true); }}>Different for each read</button>
              </div>
            )}
            {planOn ? (
              <>
                <div className="story-plan">
                  {Array.from({ length: times }, (_, i) => (
                    <label key={i} className="story-plan-row">
                      <span>Read {i + 1}{onePerDay ? ` (day ${i + 1})` : ''}</span>
                      <select value={plan[i]} onChange={e => setPlan(p => p.map((m, k) => (k === i ? e.target.value as StoryMode : m)))}>
                        {STORY_MODES.map(m => <option key={m.id} value={m.id}>{m.icon} {m.title}</option>)}
                      </select>
                    </label>
                  ))}
                </div>
                <details className="hint how">
                  <summary>What each one means</summary>
                  <ul>{STORY_MODES.map(m => <li key={m.id}><strong>{m.icon} {m.title}:</strong> {m.text}</li>)}</ul>
                </details>
              </>
            ) : (
              <div className="story-modes" role="radiogroup" aria-label="How students do the story">
                {STORY_MODES.map(m => (
                  <label key={m.id} className={`story-mode${storyMode === m.id ? ' on' : ''}`}>
                    <input type="radio" name="story-mode" checked={storyMode === m.id} onChange={() => setStoryMode(m.id)} />
                    <span><strong>{m.icon} {m.title}</strong><span className="small muted">{m.text}</span></span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {kind === 'story' && modesUsed.some(needsLesson) && (
          <div className="field">
            <label className="check-line" style={{ marginTop: 0 }}>
              <input type="checkbox" checked={oneWay} onChange={e => setOneWay(e.target.checked)} />
              <span><strong>One way only</strong>: the lesson only asks what each sentence means in {languageName(active?.native_language ?? 'en')}, never how to say it in {languageName(classLang)}</span>
            </label>
            <span className="hint">{oneWay ? 'One question per sentence: from the story into their language.' : 'Two questions per sentence: what it means, then how to say it in the story’s language.'}</span>
          </div>
        )}

        {kind === 'story' && canSplit && (
          <div className="field">
            <label className="check-line" style={{ marginTop: 0 }}>
              <input type="checkbox" checked={split} onChange={e => setSplit(e.target.checked)} />
              <span><strong>Split the story lesson across the reads</strong>: each lesson read covers the next part of the story, not all of it</span>
            </label>
            <span className="hint">
              {split
                ? `${lessonReads} parts${story.trim() ? `, about ${Math.max(1, Math.round(sentences(story).length / lessonReads))} sentences each` : ''}, one per read with a lesson, in order. Your story questions come with the last part.`
                : 'Every lesson read goes through the whole story.'}
            </span>
          </div>
        )}

        {kind === 'story' && modesUsed.some(needsReadingTime) && (
          <div className="field">
            <label htmlFor="pace">Reading pace</label>
            <select id="pace" style={{ width: 'auto' }} value={wpm} onChange={e => setWpm(Number(e.target.value))}>
              {!PACES.some(p => p.wpm === wpm) && <option value={wpm}>{paceLabel(wpm)}</option>}
              {PACES.map(p => (
                <option key={p.wpm} value={p.wpm}>{p.label}: {p.wpm} words a minute ({p.note})</option>
              ))}
            </select>
            <span className="hint">
              Slower means more time on the story before a read counts. Lower it as the class gets faster.
              Students who read faster or slower than the class can get their own share of the time on the Students page.
            </span>
          </div>
        )}

        <div className="field">
          <label className="check-line" style={{ marginTop: 0 }}>
            <input type="checkbox" checked={onePerDay} onChange={e => setOnePerDay(e.target.checked)} />
            <span><strong>One per day</strong>: each {kind === 'video' ? 'watch' : kind === 'grammar' ? 'lesson' : 'read'} has to be on a different day</span>
          </label>
          {onePerDay && (
            <span className="hint">
              {times > 1
                ? `Students need ${times} different days to finish, e.g. ${kind === 'video' ? 'watching' : kind === 'grammar' ? 'doing a lesson' : 'reading'} once a day for ${times} days. Extra ones on the same day still help them, but don't count.`
                : 'With only 1 required, this makes no difference.'}
              {due && times > 1 && daysUntil(due) + 1 < times && (
                <strong className="warn-text"> ⚠ The due date leaves only {Math.max(daysUntil(due) + 1, 0)} day{daysUntil(due) + 1 === 1 ? '' : 's'}, so students can't finish in time.</strong>
              )}
            </span>
          )}
        </div>

        <div className="field sd-rewards">
          <label>🪙 Sand dollar rewards <span className="small muted">(paid automatically, for the class ocean)</span></label>
          <div className="row sd-reward-fields">
            <label className="sd-reward">
              <span>For each {kind === 'video' ? 'watch' : kind === 'grammar' ? 'lesson' : 'read'}</span>
              <input type="number" min={0} max={100} value={sdPer} onChange={e => setSdPer(clampInt(e.target.value, 0, 100))} />
            </label>
            <label className="sd-reward">
              <span>{onePerDay && times > 1 ? `Bonus for doing it every day (all ${times} days)` : 'Bonus for finishing'}</span>
              <input type="number" min={0} max={1000} value={sdDone} onChange={e => setSdDone(clampInt(e.target.value, 0, 1000))} />
            </label>
          </div>
          <span className="hint">
            {sdPer || sdDone
              ? `A student who finishes earns 🪙 ${sdPer * times + sdDone}${sdPer && times > 1 ? `: 🪙 ${sdPer} for each of the ${times} ${kind === 'video' ? 'watches' : kind === 'grammar' ? 'lessons' : 'reads'}` : ''}${sdDone ? `${sdPer && times > 1 ? ', plus' : ':'} 🪙 ${sdDone} ${onePerDay && times > 1 ? 'for doing it every day' : 'when they finish'}` : ''}. Extra practice after that doesn’t pay more.`
              : 'Leave at 0 for no automatic reward. You can still give sand dollars by hand on the Ocean page.'}
          </span>
        </div>

        {kind === 'grammar' ? (
          <div className="field grammar-settings">
            <label>Grammar topics</label>
            {([['Verb tenses', true], ['Other topics', false]] as const).map(([heading, tense]) => {
              const group = topicsFor(classLang).filter(t => !!TOPICS[t].tense === tense);
              if (!group.length) return null;
              const picks = (
                <div className="topic-picks">
                  {group.map(t => (
                    <label key={t} className={`topic-pick${gTopics.includes(t) ? ' on' : ''}`}>
                      <input type="checkbox" checked={gTopics.includes(t)} onChange={() => toggleTopic(t)} />
                      <span className="emoji" aria-hidden>{TOPICS[t].emoji}</span>
                      <span className="grow"><strong>{TOPICS[t].title}</strong><span className="small muted">{TOPICS[t].blurb}</span></span>
                    </label>
                  ))}
                </div>
              );
              if (!tense) return <div key={heading}><div className="topic-group">{heading}</div>{picks}</div>;
              // The tenses are a long list, so they fold away; the summary says what's chosen.
              const chosen = group.filter(t => gTopics.includes(t));
              return (
                <details key={heading} className="topic-dropdown">
                  <summary>
                    <span className="topic-group">{heading}</span>
                    <span className="small muted chosen">
                      {chosen.length ? chosen.map(t => TOPICS[t].title).join(', ') : `None chosen · ${group.length} to pick from`}
                    </span>
                  </summary>
                  {picks}
                </details>
              );
            })}

            {gTopics.filter((t): t is VerbTopic => t in DEFAULT_VERBS).map(t => (
              <div key={t} className="field" style={{ marginTop: 12 }}>
                <label htmlFor={`verbs-${t}`}>Verbs for “{TOPICS[t].title}” (optional)</label>
                <input id={`verbs-${t}`} value={gVerbs[t]} spellCheck={false}
                  onChange={e => setGVerbs(v => ({ ...v, [t]: e.target.value }))}
                  placeholder={DEFAULT_VERBS[t].slice(0, 8).join(', ') + '…'} />
                <span className="hint">Leave empty for the built-in list of {DEFAULT_VERBS[t].length} common verbs.</span>
              </div>
            ))}
            {topicsFor(classLang).length === 1 && (
              <span className="hint">
                There are no ready-made topics for {languageName(classLang)} yet. Build your own lesson:
                it works exactly the same way, with hearts, typing, multiple choice and word tiles.
              </span>
            )}
            {verbIssues.length > 0 && <div className="notice bad small">{verbIssues.slice(0, 4).map(v => <div key={v}>{v}</div>)}</div>}

            {gTopics.some(t => t in DEFAULT_VERBS || t === 'gustar' || t === 'pt_present' || t.startsWith('es_t_') || t.startsWith('pt_t_')) && (
              <label className="check-line">
                <input type="checkbox" checked={gVosotros} onChange={e => setGVosotros(e.target.checked)} />
                {classLang === 'pt' ? 'Include tu (most of Brazil uses você)' : 'Include vosotros'}
              </label>
            )}

            {gTopics.includes('custom') && (
              <div className="field" style={{ marginTop: 12 }}>
                <label>Custom Lesson Builder</label>
                <span className="hint" style={{ marginBottom: 8 }}>
                  {onlyBuilder
                    ? 'Students get every question, in this order.'
                    : 'Your questions are mixed in with the topics above.'}
                  {' '}Each keeps the format you choose here.
                </span>
                <LessonBuilder items={gBuilder} onChange={setGBuilder} language={classLang} />
              </div>
            )}

            {!onlyBuilder && (<>
            <div className="field" style={{ marginTop: 12 }}>
              <label htmlFor="glevel">Difficulty</label>
              <select id="glevel" style={{ width: 'auto' }} value={gLevel}
                onChange={e => { const n = Number(e.target.value); setGLevel(n); setGTo(t => Math.max(t, Math.min(n + 1, 5))); }}>
                {LEVELS.map(l => <option key={l.n} value={l.n}>Level {l.n}: {l.name}, {l.summary.toLowerCase()}</option>)}
              </select>
              {times > 1 && gLevel < 5 && (
                <label className="check-line">
                  <input type="checkbox" checked={gClimb} onChange={e => setGClimb(e.target.checked)} />
                  Get harder with each lesson, up to
                  <select style={{ width: 'auto' }} value={gTo} disabled={!gClimb} aria-label="Last lesson's level"
                    onChange={e => setGTo(Number(e.target.value))}>
                    {LEVELS.filter(l => l.n > gLevel).map(l => <option key={l.n} value={l.n}>level {l.n} ({l.name.toLowerCase()})</option>)}
                  </select>
                </label>
              )}
              <span className="hint">
                {climbing
                  ? Array.from({ length: times }, (_, i) => `Lesson ${i + 1}: level ${levelFor(grammarConfig, i, times)}`).join(' · ') + '. Extra lessons stay at the last level.'
                  : 'Typed answers are harder than picking from options.'}
                {gTopics.includes('custom') && ' Difficulty doesn’t change your own questions: they keep the format you chose.'}
              </span>
            </div>

            <div className="field" style={{ marginTop: 12 }}>
              <label htmlFor="glen">Questions per lesson</label>
              <select id="glen" style={{ width: 'auto' }} value={gLength} onChange={e => setGLength(Number(e.target.value))}>
                {[5, 8, 10, 12, 15, 20].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            </>)}
          </div>
        ) : kind === 'video' ? (
          <div className="field">
            <label htmlFor="video">YouTube link</label>
            <input id="video" required value={video} onChange={e => setVideo(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=…" spellCheck={false} />
            {video && (videoId
              ? <img className="video-preview" src={thumbnailUrl(videoId)} alt="Video thumbnail" />
              : <span className="hint warn">That doesn’t look like a YouTube link yet.</span>)}
            {embed === 'checking' && <span className="hint">Checking that this video plays outside YouTube…</span>}
            {embed === 'ok' && <span className="hint ok">✓ This video plays on the site.</span>}
            {embed === 'unknown' && <span className="hint warn">Couldn’t confirm this video plays outside YouTube. Open the assignment after saving to check.</span>}
            {embed === 'missing' && <div className="notice bad small">This video is private or deleted. Pick a different one.</div>}
            {embed === 'blocked' && (
              <div className="notice small warn-box">
                <strong>This video only plays on YouTube.</strong> You can still assign it: students
                will watch it on youtube.com with the <strong>Gibberfish Classroom</strong> Chrome extension,
                which shows the {languageName(classLang)} subtitles and tracks their watches the same way. Students without
                the extension can't get credit for it.
              </div>
            )}
          </div>
        ) : (
          <div className="field">
            <label htmlFor="story">Story</label>
            <textarea id="story" required rows={14} value={story} onChange={e => setStory(e.target.value)}
              placeholder={`Paste the ${languageName(classLang)} story here…`} maxLength={20000} dir="auto" />
            <span className="hint">Leave a blank line between paragraphs. Students can tap any word to translate it.</span>
          </div>
        )}

        {kind === 'story' && modesUsed.some(needsLesson) && <StoryQuestions qs={storyQs} setQs={setStoryQs} lang={languageName(classLang)} />}

        {kind === 'video' && (
          <div className="field">
            <label htmlFor="subs">Subtitles in {languageName(lang)}</label>
            {autoSubs === 'loading' && <span className="hint">Getting this video’s {languageName(lang)} subtitles from YouTube…</span>}
            {autoSubs === 'ok' && <span className="hint ok">✓ Filled in automatically from the video’s own {languageName(lang)} subtitles.</span>}
            {autoSubs === 'none' && <span className="hint warn">This video has no {languageName(lang)} subtitles on YouTube. Paste a transcript below, or pick a video that has them.</span>}
            {autoSubs === 'wrong_language' && <span className="hint warn">This video only has subtitles in other languages, so none were added. Students only ever see {languageName(lang)} subtitles.</span>}
            {autoSubs === 'unavailable' && <span className="hint warn">Couldn’t fetch subtitles automatically right now. You can paste them below.</span>}
            <details className="hint how">
              <summary>Pasting them yourself instead</summary>
              <ol>
                <li>Open the video on youtube.com and click <strong>…more</strong> under the title.</li>
                <li>Scroll down and click <strong>Show transcript</strong>.</li>
                <li>If there's a language menu at the bottom of the transcript, choose <strong>{languageName(classLang)}</strong>, not your students' language.</li>
                <li>Drag from the first line to the last to select it all, copy, and paste here with the timestamps.</li>
              </ol>
              SRT or VTT subtitle files work too: open one, copy everything, paste.
            </details>
            <textarea id="subs" rows={6} value={subsText} onChange={e => setSubsText(e.target.value)}
              placeholder={'0:00\nHola a todos\n0:04\nHoy vamos a Starbucks…'} spellCheck={false} />
            {subsText.trim()
              ? (cues.length > 0
                ? <span className="hint ok">✓ {cues.length} lines, {clock(cues[0].s)} – {clock(cues[cues.length - 1].e)}. Students can tap any word in them to translate it.</span>
                : <span className="hint warn">No timestamps found yet. Paste the transcript with its times.</span>)
              : <span className="hint">Without these, the video plays with no subtitles. YouTube's own subtitles are always turned off, so nobody sees them in their own language.</span>}
          </div>
        )}

        <div className="field">
          <label htmlFor="instr">Instructions (optional)</label>
          <textarea id="instr" rows={3} value={instructions} onChange={e => setInstructions(e.target.value)}
            maxLength={4000} dir="auto"
            placeholder={classLang === 'es' ? 'Mira el video dos veces. Escribe tres palabras nuevas en tu cuaderno.' : 'Watch it twice. Write down three new words.'} />
          <span className="hint">Can be written in {languageName(classLang)}: students can tap these words to translate them too.</span>
        </div>

        {active && <ImageAttach classroomId={active.id} images={images} onChange={setImages} />}

        {error && <div className="notice bad small" style={{ marginBottom: 14 }}>{error}</div>}

        <div className="row">
          <button className="primary" disabled={busy || (kind === 'video' && (embed === 'checking' || embed === 'missing'))}
            onClick={() => { asDraft.current = false; }}>
            {busy ? 'Saving…' : embed === 'checking' && kind === 'video' ? 'Checking video…'
              : wasOut ? 'Save changes' : when === 'later' ? (existing?.publish_on && !existing.draft ? 'Save schedule' : 'Schedule') : 'Assign now'}
          </button>
          {!wasOut && (
            <button type="submit" disabled={busy || (kind === 'video' && embed === 'checking')}
              onClick={() => { asDraft.current = true; }}>
              {existing?.draft ? 'Keep as draft' : 'Save as draft'}
            </button>
          )}
          <span className="spacer" />
          {id && <button type="button" className="danger" onClick={remove} disabled={busy}>Delete</button>}
        </div>
      </form>
    </div>
  );
}

/** Saved cues back into the editable "m:ss text" form. */
function cuesToText(cues: Cue[] | null): string {
  if (!cues?.length) return '';
  return cues.map(c => `${clock(c.s)} ${c.t}`).join('\n');
}

type VerbTopic = 'present_regular' | 'present_stem' | 'preterite_regular';

/** Whole days from today (local) to a YYYY-MM-DD date. */
function daysUntil(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  const due = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}

// ── How a story is done (assignments.story_mode) ──
const STORY_MODES: { id: StoryMode; icon: string; title: string; text: string }[] = [
  { id: 'lesson', icon: '🧩', title: 'Story lesson', text: 'Students go through the story lesson first, sentence by sentence. Finishing it counts as the read, and then they can see the whole story.' },
  { id: 'free', icon: '📖🧩', title: 'Read freely, then the story lesson', text: 'The whole story is open from the start to read for as long as they like (no time limit). The read counts when they finish the story lesson.' },
  { id: 'both', icon: '🧩📖', title: 'Story lesson, then timed reading', text: 'The story lesson first; then the whole story opens and they read it with the reading time below. The read counts when both are done.' },
  { id: 'read', icon: '📖', title: 'Just read it', text: 'Timed reading only: paragraph by paragraph with the page held still, for the reading time below. No lesson.' },
];

// ── Story questions (asked at the end of the story lesson, lib/storyLesson.ts) ──
type DraftQ = { q: string; options: string[]; answer: number };

function StoryQuestions({ qs, setQs, lang }: { qs: DraftQ[]; setQs: (f: (q: DraftQ[]) => DraftQ[]) => void; lang: string }) {
  const edit = (i: number, change: Partial<DraftQ>) => setQs(list => list.map((q, k) => (k === i ? { ...q, ...change } : q)));
  return (
    <div className="field">
      <label>Story questions <span className="muted small">(optional)</span></label>
      <span className="hint">
        Multiple-choice questions about the story, asked at the end of each lesson, after the sentences. Write them in
        {` ${lang}`} or in the students’ language. A missed one comes back until they get it right; their score counts first tries.
      </span>
      {qs.map((q, i) => (
        <div key={i} className="panel story-q">
          <div className="row">
            <strong className="small">Question {i + 1}</strong>
            <span className="spacer" />
            <button type="button" className="small" onClick={() => setQs(list => list.filter((_, k) => k !== i))}>Remove</button>
          </div>
          <input dir="auto" value={q.q} maxLength={300} placeholder="e.g. ¿Adónde va Ana el sábado?" onChange={e => edit(i, { q: e.target.value })} aria-label={`Question ${i + 1}`} />
          {q.options.map((o, j) => (
            <label key={j} className="story-q-option">
              <input type="radio" name={`sq-${i}`} checked={q.answer === j} onChange={() => edit(i, { answer: j })} aria-label={`Answer ${j + 1} is right`} />
              <input dir="auto" value={o} maxLength={200} placeholder={j < 2 ? `Answer ${j + 1}` : `Answer ${j + 1} (optional)`}
                onChange={e => edit(i, { options: q.options.map((x, k) => (k === j ? e.target.value : x)) })} />
            </label>
          ))}
          <span className="hint">Tick the right answer.</span>
        </div>
      ))}
      {qs.length < 20 && (
        <button type="button" className="small" style={{ justifySelf: 'start' }} onClick={() => setQs(list => [...list, { q: '', options: ['', '', '', ''], answer: 0 }])}>
          + Add a story question
        </button>
      )}
    </div>
  );
}
